import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type { PropertyValues } from 'lit';
import { request } from '../api/request';
import { authStore } from '../store/auth-store';

interface StandardFieldRow {
  id?: number;
  message_name?: string;
  field_name: string;
  field_type: string;
  description: string;
  proto_tag: number;
}

@customElement('standard-model-view')
export class StandardModelView extends LitElement {
  static styles = css`
    :host {
      display: block;
    }
    .container {
      padding: 24px;
      max-width: 1200px;
      margin: 0 auto;
    }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
      flex-wrap: wrap;
      gap: 12px;
    }
    .header-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      align-items: center;
    }
    .card {
      background: white;
      padding: 20px;
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
      margin-bottom: 20px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 10px;
    }
    th,
    td {
      padding: 10px 8px;
      text-align: left;
      border-bottom: 1px solid #eee;
      vertical-align: middle;
    }
    th {
      background-color: #f8f9fa;
      font-weight: 600;
      color: #333;
    }
    .form-row {
      display: grid;
      grid-template-columns: 1.4fr 1.6fr 1.2fr 2fr 0.9fr auto;
      gap: 12px;
      align-items: end;
    }
    input,
    select {
      padding: 8px;
      border: 1px solid #ddd;
      border-radius: 4px;
      width: 100%;
      box-sizing: border-box;
    }
    td input {
      min-width: 0;
    }
    button {
      padding: 8px 16px;
      background-color: #4a6ee0;
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      white-space: nowrap;
    }
    button.export {
      background-color: #10b981;
    }
    button.danger {
      background-color: #ef4444;
    }
    button.muted {
      background-color: #64748b;
    }
    button.sm {
      padding: 6px 10px;
      font-size: 0.8125rem;
    }
    label.import-btn {
      display: inline-block;
      background: #f59e0b;
      color: white;
      padding: 8px 16px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 0.875rem;
      white-space: nowrap;
    }
    label.import-btn input {
      display: none;
    }
    .row-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
    }
    .group-header {
      background-color: #f1f5f9;
      font-weight: 600;
      color: #334155;
      padding: 12px 16px;
      text-align: left;
      border-bottom: 2px solid #e2e8f0;
    }
    .group-header span.badge {
      background: #3b82f6;
      color: white;
      font-size: 12px;
      padding: 2px 8px;
      border-radius: 12px;
      margin-left: 8px;
      font-weight: normal;
    }
    .edit-row-inner {
      display: flex;
      gap: 8px;
      align-items: center;
      flex-wrap: wrap;
      padding: 8px;
      background: #f8fafc;
    }
    .edit-row-inner input {
      flex: 1 1 auto;
      min-width: 4rem;
    }
  `;

  @state() private fields: StandardFieldRow[] = [];
  @state() private editId: number | null = null;
  @state() private newField = {
    message_name: 'CarStatusData',
    field_name: '',
    field_type: 'double',
    description: '',
    proto_tag: '',
  };

  protected firstUpdated(_changed: PropertyValues): void {
    super.firstUpdated(_changed);
    void this.fetchFields();
  }

  private async fetchFields() {
    try {
      const res = await request<StandardFieldRow[]>('/api/v1/fields');
      this.fields = Array.isArray(res) ? res : [];
      if (this.fields.length > 0) {
        const maxTag = Math.max(...this.fields.map((f) => Number(f.proto_tag) || 0));
        this.newField = {
          ...this.newField,
          proto_tag: String(maxTag + 1),
        };
      }
    } catch {
      /* request 已弹窗 */
    }
  }

  private async deleteField(id: number) {
    if (!confirm('确定删除?')) {
      return;
    }
    try {
      await request<unknown>(`/api/v1/fields/${id}`, { method: 'DELETE' });
      if (this.editId === id) {
        this.editId = null;
      }
      await this.fetchFields();
    } catch {
      /* request 已弹窗 */
    }
  }

  private startEdit(f: StandardFieldRow) {
    if (f.id == null) {
      return;
    }
    this.editId = f.id;
  }

  private cancelEdit() {
    this.editId = null;
  }

  private async saveEdit(f: StandardFieldRow, e: Event) {
    const btn = e.currentTarget as HTMLElement;
    const tr = btn.closest('tr');
    if (!tr || f.id == null) {
      return;
    }
    const inputs = tr.querySelectorAll('input');
    if (inputs.length < 5) {
      return;
    }
    const updated = {
      id: f.id,
      message_name: (inputs[0] as HTMLInputElement).value.trim(),
      proto_tag: parseInt((inputs[1] as HTMLInputElement).value, 10),
      field_name: (inputs[2] as HTMLInputElement).value.trim(),
      field_type: (inputs[3] as HTMLInputElement).value.trim(),
      description: (inputs[4] as HTMLInputElement).value.trim(),
    };
    if (!updated.message_name || !updated.field_name || Number.isNaN(updated.proto_tag)) {
      alert('保存失败: 所属结构、字段名和 Proto 序号不能为空');
      return;
    }
    try {
      await request<unknown>(`/api/v1/fields/${f.id}`, {
        method: 'PUT',
        body: JSON.stringify(updated),
      });
      this.editId = null;
      await this.fetchFields();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      alert(`保存失败: ${msg}`);
    }
  }

  private async handleImport(e: Event) {
    const input = e.target as HTMLInputElement;
    if (!input.files?.length) {
      return;
    }
    const formData = new FormData();
    formData.append('file', input.files[0]);
    try {
      const count = await request<number>('/api/v1/fields/import', { method: 'POST', body: formData }, true);
      alert(`成功导入 ${count} 个字段！`);
      await this.fetchFields();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      alert(`导入失败: ${msg}`);
    }
    input.value = '';
  }

  private async addField() {
    if (
      !this.newField.message_name.trim() ||
      !this.newField.field_name.trim() ||
      !this.newField.proto_tag.trim()
    ) {
      alert('所属结构、字段名和序号不能为空');
      return;
    }
    const payload = {
      message_name: this.newField.message_name.trim(),
      field_name: this.newField.field_name.trim(),
      field_type: this.newField.field_type,
      description: this.newField.description.trim(),
      proto_tag: parseInt(this.newField.proto_tag, 10),
    };
    if (Number.isNaN(payload.proto_tag)) {
      alert('Proto 序号必须是数字');
      return;
    }
    try {
      await request<unknown>('/api/v1/fields', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      this.newField = {
        ...this.newField,
        field_name: '',
        description: '',
        message_name: this.newField.message_name.trim() || 'CarStatusData',
      };
      await this.fetchFields();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      alert(`添加失败: ${msg}`);
    }
  }

  private exportProto() {
    const jwt = authStore.token;
    const u = new URL('/api/v1/fields/export', window.location.origin);
    if (jwt) {
      u.searchParams.set('token', jwt);
    }
    window.open(u.toString(), '_blank');
  }

  private getGroupTitle(msgName: string): string {
    const map: Record<string, string> = {
      RobotPacket: '📦 核心报文结构 (基础框架)',
      CarMoveControl: '🕹️ 移动控制负载 (基础业务)',
      CarLocationData: '📍 位置数据负载 (基础业务)',
      CarStatusData: '📊 状态与环境负载 (基础/扩展业务)',
      MotorState: '⚙️ 电机原子状态 (双臂业务)',
      HeadGroup: '🤖 头部组件 (双臂业务)',
      ArmGroup: '🦾 机械臂组件 (双臂业务)',
      FullRobotSnapshot: '📸 全身快照负载 (双臂业务)',
      EnvironmentData: '🍃 环境监测负载 (独立扩展业务)',
    };
    return map[msgName] || `🧩 ${msgName} (自定义业务模块)`;
  }

  private prepareFieldGroups(): {
    grouped: Record<string, StandardFieldRow[]>;
    sortedMessageNames: string[];
  } {
    const grouped: Record<string, StandardFieldRow[]> = {};
    for (const f of this.fields) {
      const key = (f.message_name ?? '').trim() || 'Unnamed';
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(f);
    }
    for (const k of Object.keys(grouped)) {
      grouped[k].sort((a, b) => (Number(a.proto_tag) || 0) - (Number(b.proto_tag) || 0));
    }
    const sortedMessageNames = Object.keys(grouped).sort((a, b) => {
      if (a === 'RobotPacket') {
        return -1;
      }
      if (b === 'RobotPacket') {
        return 1;
      }
      if (a === 'Unnamed') {
        return 1;
      }
      if (b === 'Unnamed') {
        return -1;
      }
      return a.localeCompare(b);
    });
    return { grouped, sortedMessageNames };
  }

  render() {
    const { grouped, sortedMessageNames } = this.prepareFieldGroups();

    return html`
      <div class="container">
        <div class="header">
          <h2>标准物模型 (Schema Registry)</h2>
          <div class="header-actions">
            <label class="import-btn">
              ↑ 导入 .proto 更新
              <input type="file" accept=".proto,.text" @change=${(e: Event) => this.handleImport(e)} />
            </label>
            <button type="button" class="export" @click=${() => this.exportProto()}>↓ 导出 .proto 契约文件</button>
          </div>
        </div>

        <div class="card">
          <h3>新增字段</h3>
          <div class="form-row">
            <div>
              <label>所属结构 (Message)</label>
              <input
                type="text"
                placeholder="如 CarStatusData"
                list="message_names"
                .value=${this.newField.message_name}
                @input=${(e: Event) => {
                  this.newField = {
                    ...this.newField,
                    message_name: (e.target as HTMLInputElement).value,
                  };
                }}
              />
              <datalist id="message_names">
                ${sortedMessageNames.map((g) => html`<option value=${g}></option>`)}
              </datalist>
            </div>
            <div>
              <label>字段名 (英文)</label>
              <input
                type="text"
                placeholder="如 pm25"
                .value=${this.newField.field_name}
                @input=${(e: Event) => {
                  this.newField = {
                    ...this.newField,
                    field_name: (e.target as HTMLInputElement).value,
                  };
                }}
              />
            </div>
            <div>
              <label>数据类型</label>
              <input
                type="text"
                list="field-type-options"
                placeholder="如 string / CommandType / CarFetchMapCommand"
                .value=${this.newField.field_type}
                @input=${(e: Event) => {
                  this.newField = {
                    ...this.newField,
                    field_type: (e.target as HTMLInputElement).value,
                  };
                }}
              />
              <datalist id="field-type-options">
                <option value="double"></option>
                <option value="int32"></option>
                <option value="int64"></option>
                <option value="string"></option>
                <option value="uint64"></option>
                <option value="uint32"></option>
                <option value="bool"></option>
                <option value="bytes"></option>
                <option value="RobotType"></option>
                <option value="CommandType"></option>
                <option value="StatusCode"></option>
                <option value="Priority"></option>
              </datalist>
            </div>
            <div>
              <label>中文说明</label>
              <input
                type="text"
                placeholder="如 环境PM2.5"
                .value=${this.newField.description}
                @input=${(e: Event) => {
                  this.newField = {
                    ...this.newField,
                    description: (e.target as HTMLInputElement).value,
                  };
                }}
              />
            </div>
            <div>
              <label>Proto 序号</label>
              <input
                type="number"
                .value=${this.newField.proto_tag}
                @input=${(e: Event) => {
                  this.newField = {
                    ...this.newField,
                    proto_tag: (e.target as HTMLInputElement).value,
                  };
                }}
              />
            </div>
            <button type="button" @click=${() => this.addField()}>+ 保存字段</button>
          </div>
        </div>

        <div class="card">
          <h3>已注册物模型字典</h3>
          <table>
            <thead>
              <tr>
                <th style="width:10%">Proto 序号</th>
                <th style="width:25%">字段名 (Field)</th>
                <th style="width:20%">数据类型 (Type)</th>
                <th style="width:30%">说明 (Description)</th>
                <th style="width:15%">操作</th>
              </tr>
            </thead>
            <tbody>
              ${sortedMessageNames.map((msgName) => {
                const rows = grouped[msgName] ?? [];
                return html`
                  <tr>
                    <td colspan="5" class="group-header">
                      ${this.getGroupTitle(msgName)}
                      <span class="badge">${rows.length} 个字段</span>
                    </td>
                  </tr>
                  ${rows.map(
                    (f) => html`
                      <tr>
                        ${f.id != null && this.editId === f.id
                          ? html`
                              <td colspan="5">
                                <div class="edit-row-inner">
                                  <input type="text" .value=${f.message_name ?? ''} />
                                  <input type="number" .value=${String(f.proto_tag)} />
                                  <input type="text" .value=${f.field_name} />
                                  <input type="text" .value=${f.field_type} />
                                  <input type="text" .value=${f.description ?? ''} />
                                  <button
                                    type="button"
                                    class="sm"
                                    style="background:#10b981;flex:0 0 auto;"
                                    @click=${(e: Event) => this.saveEdit(f, e)}
                                  >
                                    保存
                                  </button>
                                  <button
                                    type="button"
                                    class="sm muted"
                                    style="flex:0 0 auto;"
                                    @click=${() => this.cancelEdit()}
                                  >
                                    取消
                                  </button>
                                </div>
                              </td>
                            `
                          : html`
                              <td>${f.proto_tag}</td>
                              <td><strong>${f.field_name}</strong></td>
                              <td>${f.field_type}</td>
                              <td>${f.description}</td>
                              <td class="row-actions">
                                <button
                                  type="button"
                                  class="sm"
                                  style="background:#3b82f6;font-size:12px;"
                                  ?disabled=${f.id == null}
                                  @click=${() => this.startEdit(f)}
                                >
                                  编辑
                                </button>
                                <button
                                  type="button"
                                  class="sm danger"
                                  style="font-size:12px;"
                                  ?disabled=${f.id == null}
                                  @click=${() => f.id != null && void this.deleteField(f.id)}
                                >
                                  删除
                                </button>
                              </td>
                            `}
                      </tr>
                    `
                  )}
                `;
              })}
            </tbody>
          </table>
        </div>
      </div>
    `;
  }
}
